import { useEffect, useRef, useState } from 'react';
import { Alert, V2Button } from '@sakhtar/design-system/react';
import { apiPost } from '../../lib/apiClient.js';
import { datasetFieldLabel } from '../../lib/dataImport/datasetMapping.js';
import type { DatasetEntity } from '../../lib/types/dataImportExport.js';
interface Draft {
  field: string;
  sourceHeader: string;
  confidence: number;
  ambiguous: boolean;
}
interface Result {
  available: boolean;
  items: readonly Draft[];
  abstainedFields?: readonly string[];
}
/** Explicit header-only provider call. Proposals never alter mappings until applied. */
export function DatasetJevMapping({
  base,
  entity,
  headers,
  mapping,
  disabled,
  onApply,
  onBusyChange,
}: {
  readonly base: string;
  readonly entity: DatasetEntity;
  readonly headers: readonly string[];
  readonly mapping: Readonly<Record<string, string>>;
  readonly disabled: boolean;
  readonly onApply: (field: string, sourceHeader: string) => void;
  readonly onBusyChange: (busy: boolean) => void;
}) {
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const generation = useRef(0);
  const locked = useRef(false);
  useEffect(() => {
    generation.current += 1;
    setResult(null);
    setMessage(null);
    setBusy(false);
    locked.current = false;
    onBusyChange(false);
    return () => {
      generation.current += 1;
      onBusyChange(false);
    };
  }, [base, entity.entityClass, headers, onBusyChange]);
  async function propose() {
    if (disabled || locked.current) return;
    locked.current = true;
    setBusy(true);
    onBusyChange(true);
    const current = generation.current;
    setResult(null);
    setMessage(null);
    try {
      const response = await apiPost<Result>(`${base}/api/v1/import/mapping-proposals`, {
        entityClass: entity.entityClass,
        headers,
      });
      if (generation.current !== current) return;
      if (!response.available) {
        setMessage('پیشنهاد هوشمند فعلاً در دسترس نیست؛ نگاشت را دستی انتخاب کنید.');
        return;
      }
      if (
        !Array.isArray(response.items) ||
        response.items.some(
          (item) =>
            !entity.writableFields.includes(item.field) ||
            !headers.includes(item.sourceHeader) ||
            !Number.isFinite(item.confidence) ||
            item.confidence < 0 ||
            item.confidence > 1 ||
            typeof item.ambiguous !== 'boolean',
        )
      )
        throw new Error('Invalid draft');
      setResult(response);
    } catch {
      if (generation.current === current)
        setMessage('دریافت پیشنهاد انجام نشد؛ کمی بعد تلاش کنید یا نگاشت دستی را ادامه دهید.');
    } finally {
      if (generation.current === current) {
        locked.current = false;
        setBusy(false);
        onBusyChange(false);
      }
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Alert tone="info">
        با انتخاب این دکمه، فقط نام ستون‌ها برای پیشنهاد نگاشت به ساختار یار ارسال می‌شود. مقادیر
        ردیف‌ها ارسال نمی‌شوند؛ اعمال هر پیشنهاد با شماست.
      </Alert>
      <V2Button
        variant="secondary"
        disabled={disabled || busy || !headers.length}
        onClick={() => void propose()}
      >
        {busy ? 'در حال دریافت پیشنهاد هوشمند…' : 'دریافت پیشنهاد هوشمند'}
      </V2Button>
      {message ? <Alert tone="warning">{message}</Alert> : null}
      {result ? (
        <>
          {!result.items.length ? (
            <Alert tone="warning">پیشنهاد روشنی پیدا نشد؛ ستون‌ها را دستی انتخاب کنید.</Alert>
          ) : null}
          {result.items.map((draft) => {
            const used = Object.entries(mapping).some(
              ([field, source]) => field !== draft.field && source === draft.sourceHeader,
            );
            return (
              <div key={`${draft.field}:${draft.sourceHeader}`}>
                ستون «{draft.sourceHeader}» برای {datasetFieldLabel(draft.field)}؛ اطمینان مدل{' '}
                {(draft.confidence * 100).toLocaleString('fa-IR')}٪{' '}
                <V2Button
                  variant="ghost"
                  disabled={disabled || busy || draft.ambiguous || used}
                  onClick={() => onApply(draft.field, draft.sourceHeader)}
                >
                  {draft.ambiguous
                    ? 'نیاز به انتخاب دستی'
                    : used
                      ? 'ستون قبلاً وصل شده'
                      : 'اعمال پیشنهاد هوشمند'}
                </V2Button>
              </div>
            );
          })}
          {result.abstainedFields?.length ? (
            <p>فیلدهای بدون پیشنهاد: {result.abstainedFields.map(datasetFieldLabel).join('، ')}</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
